import React, { createContext, useCallback, useEffect, useState } from "react";
import AnswerNode from "./nodes/AnswerNode";
import DtmfNode from "./nodes/DtmfNode";
import HangupNode from "./nodes/HangupNode";
import PlayNode from "./nodes/PlayNode";
import TransferNode from "./nodes/TransferNode";
import RecordNode from "./nodes/RecordNode"
import CustomEdge from "./edges/ButtonEdge";
import ReactFlow, {
    addEdge,
    MarkerType,
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
} from "react-flow-renderer";
import { getApiCall, postApiCall, fetchAndSetFlashMessage } from '../../../../service/httpCall';
import { API_IVR_SETTINGS, API_IVR_CREATE, API_SMSWEBHOOK_LIST } from '../../../../service/url';
import { DTMF_KEYS, ACTION_COLORS, IVR_SETTING_ORDER } from '../../../../utilities/constant'
import { toast } from 'react-toastify';
import { NodesContext } from "./provider/IvrContext";
import { transformNodes, validateIVR } from "./Helpers/utils";
import { BACKEND_API_BASE_URL, FLASH_MESSAGE_ACTION_OBJECT, MODULE_ACL } from "../../../../utilities/constant";
import { getUser } from "../../../../utilities/setting";
import { WEEK_DAYS,ROUTES } from '../../../../utilities/constant';
import { useParams, useNavigate } from 'react-router-dom';
import "../../../../assets/custom/css/ivr-editor.css";
import { Row, Col, Modal, Button, Form } from 'react-bootstrap';


const nodeTypes = {
    answerNode: AnswerNode,
    playNode: PlayNode,
    gatherNode: DtmfNode,
    transferNode: TransferNode,
    hangupNode: HangupNode,
    recordNode: RecordNode
};
const edgeTypes = { custom: CustomEdge };

const NewAddIVR = ({ app }) => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [nodeConnections, setNodeConnections] = useState({});
    const [flowName, setFlowName] = useState("");
    const [actions, setActions] = useState({});
    const [error, setError] = useState("");
    const [isGatherNode, setIsGatherNode] = useState(0);
    const [alertMessage, setAlertMessage] = useState("");

    const [smsWebhookFlag, setSmsWebhookFlag] = useState(false);
    const [smsWebhookList, setSmsWebhookList] = useState([]);

    const [audioList, setAudioList] = useState([]);

    const [timeScheduleModel, setTimeScheduleModel] = useState(false);
    const [startTime, setStartTime] = useState("00:00");
    const [endTime, setEndTime] = useState("23:59");
    const [weekDays, setWeekDays] = useState([]);
    const [isWholeDay, setIsWholeDay] = useState(false);
    const [isWholeWeek, setIsWholeWeek] = useState(false);
    const [scheduleData, setScheduleData] = useState({});
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [previousTime, setPreviousTime] = useState({ start: "", end: "" });
    const [previousWeekDays, setPreviousWeekDays] = useState([]);

    const { cid } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        getUser().then((resp) => {
            if (resp) {
                setLoggedInUser(resp.id);
                getAudioPrompt(resp.id);
            }
        });
        getIvrSettings();
        addNewNode("answer");
        getSmsWebhookList();
        fetchAndSetFlashMessage(
            msg => msg.module_name === MODULE_ACL.IVR && msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD && msg.type === 'Flash Message',
            setAlertMessage
        );
    }, []);


    const getAudioPrompt = async (user_id) => {
        const resp = await getApiCall(`${BACKEND_API_BASE_URL}/v2/prompt?prompt_status=1&user_id=${user_id}`)
        const welcomeFiles = resp.filter(
            (item) =>
                item.prompt_category === "Welcome"
        );
        setAudioList(welcomeFiles);
    }

    const getIvrSettings = async () => {
        const resp = await getApiCall(API_IVR_SETTINGS)
        const desiredOrder = IVR_SETTING_ORDER;
        const actions = resp?.actions || {};

        const orderedActions = {};
        desiredOrder.forEach((key) => {
            if (actions.hasOwnProperty(key)) {
                orderedActions[key] = actions[key];
            }
        });

        setActions(orderedActions);
    }

    const getSmsWebhookList = async () => {
        const resp = await getApiCall(API_SMSWEBHOOK_LIST)
        setSmsWebhookFlag(true);
        setSmsWebhookList(resp);
    }


    useEffect(() => { updatedConnections() }, [edges])

    const updatedConnections = () => {
        const result = edges.reduce((acc, { source, target, sourceHandle }) => {
            const key = sourceHandle ?? source;
            if (!acc[key]) acc[key] = [];
            acc[key].push(target);
            return acc;
        }, {});
        setNodeConnections(result);
    }

    const generateTimeId = () => {
        return Date.now().toString(36);
    };
    const addNewNode = (type) => {
        setNodes((prev) => {
            const isFirstNode = prev.length === 0;
            const nodeOffset = 50;

            const offsetX = (prev.length % 5) * nodeOffset;
            const offsetY = (prev.length % 3) * nodeOffset;

            const finalPosition = isFirstNode
                ? { x: 500, y: 10 }
                : { x: 400 + offsetX, y: 10 + offsetY };

            if (type === "gather") {
                setIsGatherNode((c) => c + 1);
            }

            return [
                ...prev,
                {
                    id: generateTimeId(),
                    type: `${type}Node`,
                    position: finalPosition,
                    data: {
                        label: `${type} Node`,
                        verb: type === "gather" ? "dtmf" : type,
                        userId: loggedInUser,
                        apiPath: BACKEND_API_BASE_URL,
                        onRemove,
                    },
                },
            ];
        });
    };

    const updateNodeData = useCallback((nodeId, updatedData) => {
        setNodes((nds) => {
            const prevNode = nds.find(n => n.id === nodeId);
            const prevDigits = Number(prevNode?.data?.numDigits ?? 1);
            const nextDigits =
                updatedData.numDigits !== undefined
                    ? Number(updatedData.numDigits)
                    : prevDigits;

            if (prevDigits !== nextDigits) {
                setEdges((eds) =>
                    eds.filter((edge) => {
                        if (edge.source !== nodeId) return true;

                        // multi → single
                        if (prevDigits > 1 && nextDigits === 1) {
                            return edge.sourceHandle !== `${nodeId}_single_source`;
                        }

                        // single → multi
                        if (prevDigits === 1 && nextDigits > 1) {
                            return !edge.sourceHandle?.startsWith(`${nodeId}_handle-`);
                        }

                        return true;
                    })
                );
            }

            const removedHandles = [];
            if (updatedData.selectedHandles && prevNode?.data?.selectedHandles) {
                Object.keys(prevNode.data.selectedHandles).forEach((hid) => {
                    if (!updatedData.selectedHandles.hasOwnProperty(hid)) {
                        removedHandles.push(hid);
                    }
                });
            }

            if (removedHandles.length > 0) {
                setEdges((eds) =>
                    eds.filter(
                        (edge) =>
                            !(edge.source === nodeId && removedHandles.includes(edge.sourceHandle))
                    )
                );
            }

            return nds.map((node) =>
                node.id === nodeId
                    ? { ...node, data: { ...node.data, ...updatedData } }
                    : node
            );
        });
    }, [setNodes, setEdges]);

    const onRemove = (id) => {
        setNodes((n) => n.filter((x) => x.id !== id));
        setEdges((e) => e.filter((x) => x.source !== id && x.target !== id));
    };

    const setParentForTarget = useCallback((targetId, parentLabel, idToRemove) => {
        setNodes((nds) =>
            nds.map((n) =>
                n.id === targetId
                    ? { ...n, data: { ...n.data, parent: parentLabel, idToRemove } }
                    : n
            )
        );
    }, []);

    const addFlowEdge = useCallback((edgeConfig) => {
        setEdges((eds) => addEdge(edgeConfig, eds));
    }, []);

    const onConnect = useCallback((connection) => {
        const { source, target, sourceHandle } = connection;
        const sourceNode = nodes.find((n) => n.id === source);
        if (!sourceNode) return;
        const sourceVerb = sourceNode.data?.verb;

        const baseEdge = {
            ...connection,
            type: "custom",
            markerEnd: { type: MarkerType.Arrow },
        };

        if (sourceVerb === "dtmf") {
            const { numDigits = 1 } = sourceNode.data;
            if (Number(numDigits) === 1) {
                if (nodeConnections[sourceHandle]?.length > 0) {
                    toast.warning("Each DTMF option can connect only once");
                    return {};
                }
                const handleIndex = Number(sourceHandle?.split("-")[1]);
                const label = DTMF_KEYS[handleIndex]
                addFlowEdge({
                    ...baseEdge,
                    label: `dtmf: ${label}`,
                    labelStyle: { fill: "blue", fontWeight: "bold" },
                    data: {
                        label: `dtmf: ${label}`
                    }
                });

                const idToRemove = `${sourceNode.id}_handle-${label}`
                setParentForTarget(target, sourceNode.data.label, idToRemove);
                return;
            } else {
                if (nodeConnections[source]?.length >= 1) {
                    toast.warning("This node can only have one next step")
                    return;
                }

                addFlowEdge(baseEdge);
                setParentForTarget(target, sourceNode.data.label);
                return;
            }
        }

        if (sourceVerb !== "gather" && (nodeConnections[source]?.length || 0) >= 1) {
            toast.warning("A node can only have one child!")
            return {};
        }
        addFlowEdge(baseEdge);
        setParentForTarget(target, sourceNode.data.label);
    }, [nodes, nodeConnections, addFlowEdge, setParentForTarget]);

    const saveData = async () => {
        try {
            if (!validateIVR({ edges, flowName, nodes, scheduleData })) return;
            const data = transformNodes({ edges, flowName, nodesArray: nodes, isGatherNode, scheduleData, smsWebhookFlag });
            const payload = {
                nodeData: JSON.stringify(data),
                edgesData: JSON.stringify(edges),
                flowName,
                userId: cid,
                is_gather_node: isGatherNode,
                promptDuration: data.promptDuration,
                scheduleData: scheduleData
            };

            const apiIvrCreateUrl = API_IVR_CREATE.replace(':cid', cid);
            await postApiCall(apiIvrCreateUrl, payload)
            toast.success("IVR Created Successfully");
            setTimeout(() => {
                navigate(`/${ROUTES.IVR.LIST.replace(':cid', cid)}`);
            }, 1500);
        } catch (err) {
            toast.error(JSON.stringify(err) || "Something went wrong while creating IVR")
        }

    };

    const handleReset = () => {
        setNodes([]);
        setEdges([]);
        setNodeConnections({})
        setFlowName("");
        setError("");
        setIsGatherNode(0);

        addNewNode("answer");
    };

    const handleScheduleClick = () => {
        setTimeScheduleModel(true);
      
        if (scheduleData && Object.keys(scheduleData).length > 0) {
          setStartTime(scheduleData.start_time);
          setEndTime(scheduleData.end_time);
          setWeekDays(scheduleData.week_days);
      
          if (scheduleData.week_days.length === 7) {
            setIsWholeWeek(true);
          } else {
            setIsWholeWeek(false)
          }
      
          if (
            scheduleData.start_time === "00:00" &&
            scheduleData.end_time === "23:59"
          ) {
            setIsWholeDay(true);
          } else {
            setIsWholeDay(false)
          }
        }
    };

      const handleWholeDayChange = (e) => {
        const isChecked = e.target.checked;
        setIsWholeDay(isChecked); 
        if (isChecked) {
          setPreviousTime({ start: startTime, end: endTime });
          setStartTime("00:00");
          setEndTime("23:59");
        } else {
          setStartTime(previousTime.start || "");
          setEndTime(previousTime.end || "");
        }
      };
    
      const handleWholeWeekChange = (e) => {
        const isChecked = e.target.checked;
        setIsWholeWeek(isChecked); 
        if (isChecked) {
          setPreviousWeekDays(weekDays);
          setWeekDays(WEEK_DAYS);
        } else {
          setWeekDays(previousWeekDays || []);
        }
      };
    
      const handleSetSchedule = () => {
        if (endTime === "00:00" || weekDays.length === 0) {
          toast.warning("Please fill all fields")
          return; 
        }
        setScheduleData({
          start_time: startTime,
          end_time: endTime,
          week_days: weekDays
        })
        setTimeScheduleModel(false)
      };

    return (
        <>
        <NodesContext.Provider
            value={{ nodes, setNodes, smsWebhookFlag, smsWebhookList, updateNodeData, onRemove, audioList, nodeConnections }}
        >
            <div
                className="ivr-editor-page"
                style={{
                    height: "100vh",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div style={{ padding: 0, paddingRight: 80 }}>
                    {alertMessage && <div className="alert alert-success marquee-container" style={{ marginTop: "5px" }}>
                        <span className="marquee-text">{alertMessage}</span>
                    </div>}
                    <div
                        className="ivr-editor-toolbar"
                        style={{
                            padding: "15px",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <input
                                className="ivr-editor-name-input"
                                value={flowName}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFlowName(value); 

                                    let errorMessages = [];

                                    if (/\s/.test(value)) {
                                        errorMessages.push("Spaces are not allowed");
                                    }
                                    if (/\d/.test(value)) {
                                        errorMessages.push("Numbers are not allowed");
                                    }
                                    if (/[^a-zA-Z0-9\s]/.test(value)) {
                                        errorMessages.push("Special characters are not allowed");
                                    }
                                    if (errorMessages.length > 0) {
                                        setError(`flowName can only contain alphabets (A-Z, a-z). ${errorMessages.join(", ")}.`);
                                    } else {
                                        setError("");
                                    }
                                }}
                                placeholder="IVR Name"
                                style={{
                                    flex: 1,
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    width: "250px",
                                }}
                            />
                            <button
                                onClick={handleScheduleClick}
                                style={{
                                padding: "8px 16px",
                                backgroundColor: "#28A745",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                marginLeft: "5px",
                                }}
                            >
                                Time Schedule
                            </button>
                            <button
                                onClick={saveData}
                                className="btn btn-primary"
                                disabled={Boolean(error)}
                            >
                                Save IVR
                            </button>
                        </div>
                        {error && <small style={{ color: "red" }}>{error}</small>}
                        <div
                            style={{
                                position: "absolute",
                                top: "150px",
                                right: "10px",
                                zIndex: 5,
                            }}
                        >
                            <div
                                className="ivr-editor-action-panel"
                                style={{
                                    width: "100px",
                                    backgroundColor: "#fff",
                                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                                    borderRadius: "8px",
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: "5px 0",
                                }}
                            >
                                {Object.keys(actions).map((type) => (
                                    <div
                                        key={type}
                                        style={{
                                            backgroundColor: ACTION_COLORS[type] || "#007bff",
                                            color: "white",
                                            textAlign: "center",
                                            cursor:
                                                actions[type] === "Answer" ? "not-allowed" : "pointer",
                                            padding: "10px",
                                            margin: "5px 10px",
                                            borderRadius: "4px",
                                            opacity: actions[type] === "Answer" ? 0.5 : 1,
                                            transition: "background-color 0.3s",
                                        }}
                                        onClick={() => {
                                            if (actions[type] !== "Answer") {
                                                addNewNode(type);
                                            }
                                        }}
                                        onMouseEnter={(e) => {
                                            if (actions[type] !== "Answer") {
                                                e.target.style.opacity = 0.8;
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (actions[type] !== "Answer") {
                                                e.target.style.opacity = 1;
                                            }
                                        }}
                                    >
                                        {actions[type] === "Input" ? "DTMF" : actions[type]}
                                    </div>
                                ))}
                            </div>
                            <div
                                className="ivr-editor-reset"
                                style={{
                                    width: "100px",
                                    marginTop: "10px",
                                    backgroundColor: "#f8d7da",
                                    color: "#dc3545",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    fontWeight: "bold",
                                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                                    transition: "background-color 0.3s",
                                }}
                                onClick={handleReset}
                                onMouseEnter={(e) => {
                                    e.target.style.opacity = 0.8;
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.opacity = 1;
                                }}
                            >
                                Reset All
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ flexGrow: 1 }}>
                    <ReactFlowProvider>
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onConnect={onConnect}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            fitView
                        />
                    </ReactFlowProvider>
                </div>
            </div>
        </NodesContext.Provider>

        <Modal show={timeScheduleModel} onHide={() => setTimeScheduleModel(false)} size="lg" centered>
          <Modal.Header closeButton >
            <Modal.Title>Set Time Schedule</Modal.Title>
          </Modal.Header>
          <Modal.Body >
            <Row>
              <Col>
                <Form>
                  <Form.Check
                    type="checkbox"
                    label="Whole Day"
                    id="wholeDay"
                    className="mb-3"
                    checked={isWholeDay} 
                    onChange={handleWholeDayChange}
                  />
                  <Form.Group className="mb-3">
                    <Form.Label>Start Time <span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                    <Form.Control
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      disabled={isWholeDay}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>End Time <span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                    <Form.Control
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      disabled={isWholeDay}
                    />
                  </Form.Group>
                  <Form.Check
                    type="checkbox"
                    label="Whole Week"
                    id="wholeWeek"
                    className="mb-3"
                    checked={isWholeWeek}
                    onChange={handleWholeWeekChange}
                  />
                  <Form.Group className="mb-3">
                    <Form.Label>Select Days<span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                    <div>
                      {WEEK_DAYS.map((day) => (
                      <Form.Check
                        inline
                        key={day}
                        label={day}
                        type="checkbox"
                        checked={weekDays.includes(day)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setWeekDays((prev) => [...prev, day]);
                          } else {
                            setWeekDays((prev) => prev.filter((d) => d !== day));
                          }
                        }}
                        disabled={isWholeWeek}
                      />
                      ))}
                    </div>
                  </Form.Group>
                </Form>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setTimeScheduleModel(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleSetSchedule}>Set Schedule</Button>
          </Modal.Footer>
        </Modal>
        </>

    );
};
export default NewAddIVR;
